// frontend/src/components/Auth.js - Updated
import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import authService from '../services/auth/authService';
import '../styles/global.css';
import './Auth.css';

const Auth = () => {
    const { user, login } = useContext(AuthContext);
    const navigate = useNavigate();

    useEffect(() => {
        // Check the current session when the component mounts
        const checkSession = async () => {
            try {
                // Initialize the auth service
                await authService.initialize();
                
                // Get current user instead of direct supabase call
                const currentUser = await authService.getCurrentUser();
                if (currentUser) {
                    login(currentUser);
                    navigate('/');
                }
            } catch (error) {
                console.error("Session check error:", error);
            }
        };
        checkSession();

        // Add auth state change listener instead of direct supabase call
        const removeListener = authService.addAuthStateChangeListener((event) => {
            if (event.type === 'SIGNED_IN') {
                login(event.user);
                localStorage.setItem('token', event.access_token || event.user.access_token);
                navigate('/');
            } else if (event.type === 'SIGNED_OUT') {
                localStorage.removeItem('token');
            }
        });

        // Clean up listener on unmount
        return () => {
            if (removeListener) removeListener();
            authService.cleanup();
        };
    }, [login, navigate]);

    const signInWithGoogle = async () => {
        try {
            // Use authService method instead of direct supabase call
            const result = await authService.signInWithGoogle();
            
            // Handle redirect URL if returned
            if (result && result.url) {
                window.location.href = result.url;
            }
        } catch (err) {
            console.error("Error signing in:", err);
        }
    };

    return (
        <div className="auth-container">
            {/* Left Side - Visual */}
            <div className="auth-visual-side">
                <div className="auth-visual-header">
                    <h1>
                        <span className="header-emoji">😛</span>
                        Face with Tongue
                    </h1>
                    <p>Effortlessly translate your documents with AI-powered precision and maintain full control.</p>
                    
                    <ul className="auth-feature-list">
                        <li>Unlike Google Translate or ChatGPT, we preserve Markdown, LaTeX, and other annotations</li>
                        <li>Custom glossaries ensure terminology consistency</li>
                        <li>$10 for approximately 10,000 words of translation</li>
                        <li>Buy credits as you go</li>
                    </ul>
                </div>
                {/* Removed the auth-illustration-container with large emoji */}
            </div>

            {/* Right Side - Login Form */}
            <div className="auth-form-side">
                <div className="auth-form-content">

                    {/* Mobile Header */}
                    <div className="auth-mobile-header">
                        <div className="auth-mobile-emoji">😛</div>
                        <div className="auth-mobile-title">Face with Tongue</div>
                    </div>

                    <h2 className="auth-form-heading">Welcome</h2>
                    <p className="auth-form-subheading">
                        {user ? 'You are already logged in.' : 'Sign in to manage your translation projects.'}
                    </p>

                    {user ? (
                        <div className="auth-logged-in-card">
                            <p>Signed in as:</p>
                            <p>{user.email}</p>
                            <button
                                onClick={() => navigate('/')}
                                className="btn btn-primary w-full"
                            >
                                Go to Your Projects
                            </button>
                        </div>
                    ) : (
                       <>
                            <button
                                onClick={signInWithGoogle}
                                className="btn-google"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-.97 2.53-1.94 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.16H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.84l3.66-2.75z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.16l3.66 2.75c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                    <path d="M1 1h22v22H1z" fill="none"/>
                                </svg>
                                Sign in with Google
                            </button>

                            <div className="auth-disclaimer">
                                <p>
                                    By signing in, you agree to our {/* Replace anchor tags with spans */}
                                    <span className="text-primary cursor-pointer">Terms of Service</span> and{' '}
                                    <span className="text-primary cursor-pointer">Privacy Policy</span>.
                                </p>
                            </div>
                       </>
                   )}
                </div>
            </div>
        </div>
    );
};

export default Auth;